import { boardProgress, showsColumnProgress } from '../../extensions/bundled/task-manager/boardProgress';
import CloudFullModal from '../ui/CloudFullModal';
import ShareBoardModal from './ShareBoardModal';
import MakeBoardPrivateModal from './MakeBoardPrivateModal';
import { Plus } from 'lucide-react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { ConfirmModal } from '../ui/Modal';
import TaskCard from '../TaskCard';
import BoardsHome from './BoardsHome';
import TaskDetailModal from '../TaskDetailModal';
import OsScroll from '../ui/OsScroll';
import BoardFilterBar from '../BoardFilterBar';
import ShareTaskToChatModal from '../ShareTaskToChatModal';
import SyncTooLargeBanner from '../SyncTooLargeBanner';
import { AddListDropZone } from './ColumnDropZone';
import Column from './KanbanColumn';
import TaskListRow from './TaskListRow';
import ListView from './ListView';
import ListSortControl from './ListSortControl';
import BoardToolbar from './BoardToolbar';
import i18n from '../../i18n';
import BoardEditModal from './BoardEditModal';
import { NO_SENSORS } from './taskBoardTabConstants';
import { prefixesTakenByOthers } from '../../extensions/bundled/task-manager/taskKeyPrefixEdit';

/** The boards grid or the open board's view. Plain function called during render, so the element tree is unchanged. */
export function renderTaskBoardTab({
  board,
  boards,
  roleForTeam,
  boardFilter,
  boardId,
  isActive,
  cardsMayDefer,
  boardsLoading,
  setBoardFilter,
  shareableTeams,
  teamById,
  boardLoading,
  selectedTaskId,
  setSelectedTaskId,
  deleteGroupTarget,
  setDeleteGroupTarget,
  boardModal,
  setBoardModal,
  shareBoardTarget,
  setShareBoardTarget,
  privateBoardTarget,
  setPrivateBoardTarget,
  deleteBoardTarget,
  setDeleteBoardTarget,
  updateBoard,
  boardCloudReadOnly,
  boardReadOnly,
  currentUserId,
  boardViewers,
  activeDragId,
  redo,
  canUndo,
  canRedo,
  justCreatedTaskIdsRef,
  justCreatedGroupIdsRef,
  saveState,
  undo,
  sensors,
  loadBoards,
  boardFilterRef,
  boardTeamId,
  boardMembers,
  memberById,
  shareTaskTarget,
  setShareTaskTarget,
  assignCtx,
  boardPalette,
  labelSource,
  isFiltering,
  taskVisible,
  sortedGroups,
  groupIds,
  showDoneTasks,
  groupTasks,
  filterBarTasks,
  handleDragEnd,
  handleDragCancel,
  openCreateBoardModal,
  openEditBoardModal,
  requestDeleteFromModal,
  movingBoardId,
  cloudFull,
  setCloudFull,
  canMoveLocation,
  moveBoardLocation,
  submitBoardModal,
  confirmDeleteBoard,
  handleAddListInline,
  handleDeleteGroup,
  handleRenameGroup,
  handleAddTask,
  handleChangeTask,
  handleAddLabelToTask,
  handleMoveTaskToList,
  handleDeleteTask,
  handleDragOver,
  handleDragStart,
  collisionDetectionStrategy,
}) {
  // Everything below renders as one tree (grid + kanban) with views toggled via
  // `hidden`, so the kanban's DndContext stays mounted across grid<->board and
  // board<->board switches. Unmounting it makes dnd-kit re-init and visibly
  // reposition the cards. Board-deref consts are guarded because `board` can be
  // null (or held over from a previous board) while the grid is showing.
  const showGrid = !boardId;
  const kanbanLoading = !showGrid && (boardLoading || !board);
  const selectedTask = board && selectedTaskId ? board.tasks.find((t) => t.id === selectedTaskId) : null;
  const activeTask = board && activeDragId?.startsWith('task:') ? board.tasks.find((t) => `task:${t.id}` === activeDragId) : null;
  const activeGroup = board && activeDragId?.startsWith('group:') ? board.groups.find((g) => `group:${g.id}` === activeDragId) : null;
  const viewMode = board?.viewMode === 'list' ? 'list' : 'kanban';
  // showDoneTasks (above): the board-level toggle from the settings modal.
  // Absent/undefined on older boards → completed tasks are shown.
  // "3 of 47": the denominator is what the board would show with no filter (so
  // the board-level "show completed" toggle is already applied to both), the
  // numerator what survives the filter. Without it a filtered board is
  // indistinguishable from an empty one.
  const shownAfterDone = board ? board.tasks.filter((t) => showDoneTasks || !t.done) : [];
  const filteredCount = isFiltering ? shownAfterDone.filter(taskVisible).length : shownAfterDone.length;

  return (
    <>
      {/* ---- Boards home ---- */}
      {showGrid && (
      <div className="task-board flex flex-col h-full min-h-0">
        {boardsLoading ? (
          <div className="task-board-empty">
            <span className="env-save-spinner" />
          </div>
        ) : (
          <BoardsHome
            boards={boards}
            boardFilter={boardFilter}
            setBoardFilter={setBoardFilter}
            boardFilterRef={boardFilterRef}
            teamById={teamById}
            roleForTeam={roleForTeam}
            shareableTeams={shareableTeams}
            canMoveLocation={canMoveLocation}
            moveBoardLocation={moveBoardLocation}
            movingBoardId={movingBoardId}
            openEditBoardModal={openEditBoardModal}
            openCreateBoardModal={openCreateBoardModal}
            setDeleteBoardTarget={setDeleteBoardTarget}
            setShareBoardTarget={setShareBoardTarget}
            setPrivateBoardTarget={setPrivateBoardTarget}
          />
        )}

        <CloudFullModal isOpen={!!cloudFull} kind={cloudFull || 'boards'} onClose={() => setCloudFull(null)} />

        {shareBoardTarget && (
          <ShareBoardModal
            target={shareBoardTarget}
            teams={shareableTeams}
            onChange={setShareBoardTarget}
            onClose={() => setShareBoardTarget(null)}
            onShared={() => {
              setShareBoardTarget(null);
              loadBoards();
            }}
          />
        )}

        {privateBoardTarget && (
          <MakeBoardPrivateModal
            target={privateBoardTarget}
            teamName={teamById.get(String(privateBoardTarget.teamId))?.name}
            onClose={() => setPrivateBoardTarget(null)}
            onDone={() => {
              setPrivateBoardTarget(null);
              loadBoards();
            }}
          />
        )}
      </div>
      )}

      {/* Kanban loading spinner, shown in place of the kanban */}
      {kanbanLoading && (
        <div className="task-board flex flex-col h-full min-h-0">
          <div className="task-board-empty">
            <span className="env-save-spinner" />
          </div>
        </div>
      )}

      {/* ---- Open board (kanban): kept mounted (hidden) across switches ---- */}
      {/* Inline display (not `hidden`) because the `flex` utility would override
          the [hidden] attribute and leave the kanban visible over the grid. */}
      {board && (
      <div className={`task-board task-board--open flex flex-col h-full min-h-0${viewMode === 'list' ? ' task-board--list' : ''}${activeDragId ? ' task-board--dragging' : ''}${cardsMayDefer ? ' task-board--cards-defer' : ''}`} style={{ display: showGrid || kanbanLoading ? 'none' : undefined }}>
      <SyncTooLargeBanner scopeId={board.id} kind="board" />
      <BoardToolbar board={board} boardReadOnly={boardReadOnly} readOnlyReason={boardCloudReadOnly ? i18n.t('cloudLocal.readOnly') : null} boardViewers={boardViewers} canRedo={canRedo} canUndo={canUndo} openEditBoardModal={openEditBoardModal} progress={boardProgress(board)} redo={redo} undo={undo} updateBoard={updateBoard} viewMode={viewMode}>
      <div className={`task-board-filter-row${viewMode === 'list' ? ' task-board-filter-row--list' : ''}`}>
        <BoardFilterBar
          isTeamBoard={!!boardTeamId}
          shown={filteredCount}
          total={shownAfterDone.length}
          palette={boardPalette}
          tasks={filterBarTasks}
          showDoneTasks={showDoneTasks}
          currentUserId={currentUserId}
          hotkeyActive={isActive && !showGrid && !kanbanLoading}
        />
        {viewMode === 'list' && <ListSortControl boardId={board.id} />}
      </div>
      </BoardToolbar>
      <DndContext
        sensors={isFiltering ? NO_SENSORS : sensors}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {viewMode === 'kanban' ? (
          <SortableContext items={groupIds} strategy={horizontalListSortingStrategy}>
            <OsScroll className={`task-board-columns flex-1 min-h-0${isFiltering ? ' task-board-scroll--filtered' : ''}`}>
              {sortedGroups.map((group, index) => (
                <Column
                  key={group.id}
                  group={group}
                  index={index}
                  progress={groupTasks.get(group.id).progress}
                  showProgress={showsColumnProgress(board)}
                  tasks={groupTasks.get(group.id).tasks}
                  memberById={memberById}
                  assignCtx={assignCtx}
                  palette={labelSource}
                  onOpenTask={setSelectedTaskId}
                  onAddTask={handleAddTask}
                  onDeleteGroup={setDeleteGroupTarget}
                  onRenameGroup={handleRenameGroup}
                  onChangeTask={handleChangeTask}
                  onDeleteTask={handleDeleteTask}
                  justCreatedRef={justCreatedTaskIdsRef}
                  justCreatedGroupsRef={justCreatedGroupIdsRef}
                />
              ))}
              <AddListDropZone className="task-board-add-column-btn" onClick={handleAddListInline}>
                <Plus size={14} /> Add list
              </AddListDropZone>
            </OsScroll>
          </SortableContext>
        ) : (
          <ListView
            board={board}
            groups={sortedGroups}
            showDoneTasks={showDoneTasks}
            taskVisible={taskVisible}
            isFiltering={isFiltering}
            isTeamBoard={!!boardTeamId}
            selectedTaskId={selectedTaskId}
            memberById={memberById}
            assignCtx={assignCtx}
            onOpenTask={setSelectedTaskId}
            onAddTask={handleAddTask}
            onAddList={handleAddListInline}
            onDeleteGroup={setDeleteGroupTarget}
            onRenameGroup={handleRenameGroup}
            onChangeTask={handleChangeTask}
            onDeleteTask={handleDeleteTask}
            justCreatedRef={justCreatedTaskIdsRef}
            justCreatedGroupsRef={justCreatedGroupIdsRef}
          />
        )}
        <DragOverlay>
          {activeTask ? (
            viewMode === 'list' ? (
              <TaskListRow task={activeTask} isOverlay onChange={() => {}} onDelete={() => {}} onOpenDetail={() => {}} />
            ) : (
              <TaskCard task={activeTask} isOverlay onChange={() => {}} onDelete={() => {}} onOpenDetail={() => {}} />
            )
          ) : activeGroup ? (
            <div className={viewMode === 'list' ? 'task-board-list-section' : 'task-board-column'} style={{ opacity: 0.9 }}>
              <div className="task-board-column-header">
                <span className={`task-board-column-title truncate${activeGroup.name ? '' : ' task-board-column-title--empty'}`}>{activeGroup.name || 'Unnamed list'}</span>
                <span className="task-board-column-count">
                  {board.tasks.filter((t) => t.groupId === activeGroup.id).length}
                </span>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ConfirmModal
        isOpen={!!deleteGroupTarget}
        onClose={() => setDeleteGroupTarget(null)}
        onConfirm={handleDeleteGroup}
        title="Delete list"
        message={`Delete "${deleteGroupTarget?.name || 'Untitled'}" and all its tasks? This can't be undone.`}
        confirmText="Delete"
        type="danger"
      />

      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          saveState={saveState}
          teamId={board.visibility === 'team' && board.teamId != null ? board.teamId : null}
          boardId={board.id}
          boardLabels={board.labels}
          isActive={isActive}
          onClose={() => setSelectedTaskId(null)}
          onChange={(patch) => handleChangeTask(selectedTask.id, patch)}
          onDelete={() => handleDeleteTask(selectedTask.id)}
          lists={board.groups}
          onAddLabel={(name) => handleAddLabelToTask(selectedTask.id, name)}
          onMove={(groupId) => handleMoveTaskToList(selectedTask.id, groupId)}
        />
      )}
      {/* One instance for the whole board rather than one per card: 60 cards
          would otherwise mount 60 modals, and only one can ever be open. */}
      {shareTaskTarget && board && (
        <ShareTaskToChatModal
          isOpen
          onClose={() => setShareTaskTarget(null)}
          task={shareTaskTarget}
          board={board}
          members={boardMembers}
        />
      )}
      </div>
      )}

      {/* Board create/edit modal. Lives at the fragment root (not inside the
          boards-grid branch) so it can be opened both from a board tile and from
          the settings gear in an open board's toolbar. */}
      {boardModal && (
        <BoardEditModal
          boardModal={boardModal}
          setBoardModal={setBoardModal}
          shareableTeams={shareableTeams}
          takenPrefixes={prefixesTakenByOthers(boards, boardModal.id)}
          submitBoardModal={() => submitBoardModal({ takenPrefixes: prefixesTakenByOthers(boards, boardModal.id) })}
          onDeleteBoard={requestDeleteFromModal}
        />
      )}
      {/* At the root too: "Delete board" is offered by a tile AND by the edit
          modal's footer, which an open board shows (list 5). */}
      <ConfirmModal
        isOpen={!!deleteBoardTarget}
        onClose={() => setDeleteBoardTarget(null)}
        onConfirm={confirmDeleteBoard}
        title="Delete board"
        message={`Delete "${deleteBoardTarget?.name}" and all its lists/tasks? This can't be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </>
  );
}
